import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { State, City } from 'country-state-city';
import { api_url } from "../Hooks/Global";

const ATCRegistrationForm = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isGeolocationFetched, setIsGeolocationFetched] = useState(false);

    const apiURL = api_url;

    const [formData, setFormData] = useState({
        franchise_name: '',
        franchise_contact_number: '',
        franchise_email_address: '',
        franchise_address: '',
        franchise_city: '',
        franchise_district: '',
        franchise_state: '',
        franchise_pincode: '',
        franchise_no_rooms: '',
        franchise_no_of_employees: '',
        franchise_internet_connection: '1',
        franchise_printer_scanner: '1',
        franchise_inverter: '1',
        franchise_logo: null,
        franchise_total_area: '',
        registration_type: '',
        latitude: '',
        longitude: '',
        franchise_dg_pin: ''
    });

    const [institute_type, setInstituteType] = useState(null);
    const [errors, setErrors] = useState({});
    const [registrationType, setRegistrationType] = useState(null);

    const franchise_type_options = [
        { value: "Proprietorship Firm", label: "Proprietorship Firm" },
        { value: "Partnership Firm", label: "Partnership Firm" },
        { value: "NGO", label: "NGO" },
        { value: "PVT LTD.", label: "PVT LTD." },
        { value: "Other", label: "Other" },
    ];

    const registrationTypeOptions = [
        { value: "Basic", label: "Basic" },
        { value: "Standard-I", label: "Standard-I" },
        { value: "Standard-II", label: "Standard-II" },
        { value: "Premium", label: "Premium" }
    ];

    const states = State.getStatesOfCountry("IN").map((state) => ({
        value: state.isoCode,
        label: state.name
    }));

    const districts = formData?.franchise_state
        ? City.getCitiesOfState(
            "IN",
            states.find(s => s.label === formData?.franchise_state)?.value
        ).map((city) => ({
            value: city.name,
            label: city.name
        }))
        : [];

    // Fetch Geolocation
    useEffect(() => {
        const fetchGeolocation = () => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        setFormData((prev) => ({
                            ...prev,
                            latitude: position.coords.latitude.toString(),
                            longitude: position.coords.longitude.toString(),
                        }));
                        setIsGeolocationFetched(true);
                    },
                    (error) => {
                        console.warn("Error fetching location:", error);
                        setFormData((prev) => ({
                            ...prev,
                            latitude: "0",
                            longitude: "0",
                        }));
                        setIsGeolocationFetched(true);
                    }
                );
            } else {
                console.warn("Geolocation not supported.");
                setFormData((prev) => ({
                    ...prev,
                    latitude: "0",
                    longitude: "0",
                }));
                setIsGeolocationFetched(true);
            }
        };

        if (!isGeolocationFetched) {
            fetchGeolocation();
        }
    }, [isGeolocationFetched]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const maxSize = 1 * 1024 * 1024;
        if (file.size > maxSize) {
            setErrors((prev) => ({
                ...prev,
                franchise_logo: "File size should not exceed 1 MB",
            }));
            setFormData((prev) => ({ ...prev, franchise_logo: null }));
            e.target.value = "";
            return;
        }

        setFormData((prev) => ({
            ...prev,
            franchise_logo: file,
        }));
        setErrors((prev) => ({ ...prev, franchise_logo: "" }));
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleInstituteTypeChange = (selectedOption) => {
        setInstituteType(selectedOption?.value);
        setErrors(prev => ({ ...prev, institute_type: "" }));
    };

    const handleRegistrationTypeChange = (selectedOption) => {
        setRegistrationType(selectedOption);
        setFormData(prev => ({
            ...prev,
            registration_type: selectedOption ? selectedOption.value : ""
        }));
        setErrors(prev => ({ ...prev, registration_type: "" }));
    };

    const validateForm = () => {
        let newErrors = {};

        // Franchise Info
        if (!formData?.franchise_name?.trim()) {
            newErrors.franchise_name = "Franchise Name is required";
        }
        if (!formData?.franchise_contact_number?.trim()) {
            newErrors.franchise_contact_number = "Contact Number is required";
        } else if (!/^\d{10}$/.test(formData.franchise_contact_number.trim())) {
            newErrors.franchise_contact_number = "Contact Number must be 10 digits";
        }
        if (!formData?.franchise_email_address?.trim()) {
            newErrors.franchise_email_address = "Email Address is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.franchise_email_address.trim())) {
            newErrors.franchise_email_address = "Please enter a valid email address";
        }
        if (!institute_type) {
            newErrors.institute_type = "Institute Type is required";
        }
        if (!formData?.franchise_dg_pin?.trim()) {
            newErrors.franchise_dg_pin = "Franchise DG Pin is required";
        }
        if (!formData?.registration_type) {
            newErrors.registration_type = "Registration Type is required";
        }

        // Address
        if (!formData?.franchise_address?.trim()) {
            newErrors.franchise_address = "Address is required";
        }
        if (!formData?.franchise_city?.trim()) {
            newErrors.franchise_city = "City is required";
        }
        if (!formData?.franchise_district?.trim()) {
            newErrors.franchise_district = "District is required";
        }
        if (!formData?.franchise_state?.trim()) {
            newErrors.franchise_state = "State is required";
        }
        if (!formData?.franchise_pincode?.trim()) {
            newErrors.franchise_pincode = "Pincode is required";
        } else if (!/^\d{6}$/.test(formData.franchise_pincode.trim())) {
            newErrors.franchise_pincode = "Pincode must be 6 digits";
        }
        if (!formData?.franchise_total_area?.trim()) {
            newErrors.franchise_total_area = "Total Area is required";
        }

        // Facilities
        if (!formData?.franchise_no_rooms?.trim()) {
            newErrors.franchise_no_rooms = "No. of Rooms is required";
        }
        if (!formData?.franchise_no_of_employees?.trim()) {
            newErrors.franchise_no_of_employees = "No. of Employees is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const isValid = validateForm();
        if (!isValid) {
            Swal.fire({
                title: "Validation Error",
                text: "Please fill in all required fields correctly",
                icon: "error"
            });
            return;
        }

        try {
            setLoading(true);
            const body = new FormData();

            // Append all form data
            body.append("franchise_name", formData.franchise_name || "");
            body.append("franchise_contact_number", formData.franchise_contact_number || "");
            body.append("franchise_email_address", formData.franchise_email_address || "");
            body.append("franchise_address", formData.franchise_address || "");
            body.append("franchise_city", formData.franchise_city || "");
            body.append("franchise_district", formData.franchise_district || "");
            body.append("franchise_state", formData.franchise_state || "");
            body.append("franchise_pincode", formData.franchise_pincode || "");
            body.append("franchise_total_area", formData.franchise_total_area || "");
            body.append("franchise_no_of_employees", formData.franchise_no_of_employees || "");
            body.append("franchise_no_rooms", formData.franchise_no_rooms || "");
            body.append("registration_type", formData.registration_type || "");
            body.append("franchise_internet_connection", formData.franchise_internet_connection || "1");
            body.append("franchise_printer_scanner", formData.franchise_printer_scanner || "1");
            body.append("franchise_inverter", formData.franchise_inverter || "1");
            body.append("franchise_dg_pin", formData.franchise_dg_pin || "");
            body.append("latitude", formData.latitude || "0");
            body.append("longitude", formData.longitude || "0");
            body.append("institute_type", institute_type || "");
            body.append("latlong", `${formData.latitude || ""},${formData.longitude || ""}`);

            if (formData.franchise_logo && formData.franchise_logo instanceof File) {
                body.append("franchise_logo", formData.franchise_logo);
            }

            const response = await axios.post(`${apiURL}Franchise/RegisterFranchise`, body, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response?.data?.success) {
                Swal.fire({
                    title: "Success",
                    html: response.data.message,
                    icon: "success",
                    confirmButtonText: "Continue"
                }).then((result) => {
                    if (result.isConfirmed) {
                        const newFranchiseId = response?.data?.franchise_id;
                        navigate(`/FranchiseDirector/${newFranchiseId}`);
                    }
                });
            } else {
                Swal.fire({
                    title: "Error",
                    text: response.data.message || "Failed to save franchise",
                    icon: "error"
                });
            }
        } catch (error) {
            console.error('Submission error:', error);
            Swal.fire({
                title: "Error",
                text: error.response?.data?.message || "An unexpected error occurred",
                icon: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div id='wrapper'>
            <div className="inner-page-banner-area" style={{ backgroundImage: 'url("/images/About_images/about_banner.jpg")' }}>
                <div className="container">
                    <div className="pagination-area">
                        <h1>Franchise Registration Application</h1>
                    </div>
                </div>
            </div>

            <div className="registration-page-area">
                <div className="container">
                    <h2 className="sidebar-title">Register</h2>
                    <div className="registration-details-area inner-page-padding">
                        <form onSubmit={handleSubmit}>
                            {/* Franchise Info Section */}
                            <div className="row">
                                <div className="col-lg-12">
                                    <h4 className="mb-3">Franchise Information</h4>
                                    <hr />
                                </div>
                            </div>

                            <div className="row">

                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Franchise Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter franchise name"
                                            name="franchise_name"
                                            value={formData.franchise_name}
                                            onChange={handleInputChange}
                                        />
                                        {errors.franchise_name && <small className="text-danger">{errors.franchise_name}</small>}
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Institute Type <span className="text-danger">*</span></label>
                                        <Select
                                            options={franchise_type_options}
                                            placeholder="Select institute type"
                                            value={franchise_type_options?.find(item => item?.value === institute_type)}
                                            onChange={handleInstituteTypeChange}
                                            isClearable
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                        {errors.institute_type && <small className="text-danger">{errors.institute_type}</small>}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Registration Type <span className="text-danger">*</span></label>
                                        <Select
                                            options={registrationTypeOptions}
                                            value={registrationType}
                                            onChange={handleRegistrationTypeChange}
                                            placeholder="Select Registration Type"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                        {errors.registration_type && <small className="text-danger">{errors.registration_type}</small>}
                                    </div>
                                </div>

                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Franchise Contact Number <span className="text-danger">*</span></label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            placeholder="Enter contact number"
                                            name="franchise_contact_number"
                                            value={formData.franchise_contact_number}
                                            onChange={handleInputChange}
                                            maxLength="10"
                                        />
                                        {errors.franchise_contact_number && <small className="text-danger">{errors.franchise_contact_number}</small>}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Franchise Email Address <span className="text-danger">*</span></label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            placeholder="Enter email address"
                                            name="franchise_email_address"
                                            value={formData.franchise_email_address}
                                            onChange={handleInputChange}
                                        />
                                        {errors.franchise_email_address && <small className="text-danger">{errors.franchise_email_address}</small>}
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Franchise DG Pin <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter franchise DG pin"
                                            name="franchise_dg_pin"
                                            value={formData.franchise_dg_pin}
                                            onChange={handleInputChange}
                                        />
                                        {errors.franchise_dg_pin && <small className="text-danger">{errors.franchise_dg_pin}</small>}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-12 col-md-12 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Franchise Logo</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="form-control"
                                            onChange={handleFileChange}
                                        />
                                        {formData.franchise_logo && (
                                            <div className="mt-2">
                                                <strong>Selected:</strong> {formData.franchise_logo.name}
                                            </div>
                                        )}
                                        {errors.franchise_logo && <small className="text-danger">{errors.franchise_logo}</small>}
                                    </div>
                                </div>
                            </div>

                            {/* Address Section */}
                            <div className="row mt-4">
                                <div className="col-lg-12">
                                    <h4 className="mb-3">Address Information</h4>
                                    <hr />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">City/Taluka <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter city"
                                            name="franchise_city"
                                            value={formData.franchise_city}
                                            onChange={handleInputChange}
                                        />
                                        {errors.franchise_city && <small className="text-danger">{errors.franchise_city}</small>}
                                    </div>
                                </div>

                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Pincode <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter pincode"
                                            name="franchise_pincode"
                                            value={formData.franchise_pincode}
                                            onChange={handleInputChange}
                                            maxLength="6"
                                        />
                                        {errors.franchise_pincode && <small className="text-danger">{errors.franchise_pincode}</small>}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">State <span className="text-danger">*</span></label>
                                        <Select
                                            options={states}
                                            placeholder="Search state..."
                                            value={states.find(s => s.label === formData?.franchise_state)}
                                            onChange={(selected) => {
                                                handleInputChange({
                                                    target: {
                                                        name: "franchise_state",
                                                        value: selected ? selected.label : ""
                                                    }
                                                });
                                                handleInputChange({
                                                    target: {
                                                        name: "franchise_district",
                                                        value: ""
                                                    }
                                                });
                                            }}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                        {errors.franchise_state && <small className="text-danger">{errors.franchise_state}</small>}
                                    </div>
                                </div>

                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">District <span className="text-danger">*</span></label>
                                        <Select
                                            options={districts}
                                            placeholder="Search district..."
                                            value={districts.find(d => d.value === formData?.franchise_district)}
                                            onChange={(selected) =>
                                                handleInputChange({
                                                    target: {
                                                        name: "franchise_district",
                                                        value: selected ? selected.value : ""
                                                    }
                                                })
                                            }
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                        {errors.franchise_district && <small className="text-danger">{errors.franchise_district}</small>}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Latitude <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter latitude"
                                            name="latitude"
                                            value={formData.latitude}
                                            onChange={handleInputChange}
                                            readOnly
                                        />
                                        {errors.latitude && <small className="text-danger">{errors.latitude}</small>}
                                    </div>
                                </div>

                                <div className="col-lg-6 col-md-6 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Longitude <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter longitude"
                                            name="longitude"
                                            value={formData.longitude}
                                            onChange={handleInputChange}
                                            readOnly
                                        />
                                        {errors.longitude && <small className="text-danger">{errors.longitude}</small>}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-12 col-md-12 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Full Address <span className="text-danger">*</span></label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            placeholder="Enter complete address"
                                            name="franchise_address"
                                            value={formData.franchise_address}
                                            onChange={handleInputChange}
                                        ></textarea>
                                        {errors.franchise_address && <small className="text-danger">{errors.franchise_address}</small>}
                                    </div>
                                </div>
                            </div>

                            {/* Facilities Section */}
                            <div className="row mt-4">
                                <div className="col-lg-12">
                                    <h4 className="mb-3">Facilities Information</h4>
                                    <hr />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-4 col-md-4 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Internet Connection <span className="text-danger">*</span></label>
                                        <div className="d-flex gap-3 mt-2">
                                            <div className="form-check">
                                                <input
                                                    className=""
                                                    type="radio"
                                                    name="franchise_internet_connection"
                                                    value="1"
                                                    checked={formData.franchise_internet_connection === "1"}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label">Yes</label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className=""
                                                    type="radio"
                                                    name="franchise_internet_connection"
                                                    value="0"
                                                    checked={formData.franchise_internet_connection === "0"}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label">No</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-4 col-md-4 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Printer/Scanner <span className="text-danger">*</span></label>
                                        <div className="d-flex gap-3 mt-2">
                                            <div className="form-check">
                                                <input
                                                    className=""
                                                    type="radio"
                                                    name="franchise_printer_scanner"
                                                    value="1"
                                                    checked={formData.franchise_printer_scanner === "1"}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label">Yes</label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className=""
                                                    type="radio"
                                                    name="franchise_printer_scanner"
                                                    value="0"
                                                    checked={formData.franchise_printer_scanner === "0"}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label">No</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-4 col-md-4 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Inverter <span className="text-danger">*</span></label>
                                        <div className="d-flex gap-3 mt-2">
                                            <div className="form-check">
                                                <input
                                                    className=""
                                                    type="radio"
                                                    name="franchise_inverter"
                                                    value="1"
                                                    checked={formData.franchise_inverter === "1"}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label">Yes</label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className=""
                                                    type="radio"
                                                    name="franchise_inverter"
                                                    value="0"
                                                    checked={formData.franchise_inverter === "0"}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label">No</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-4 col-md-4 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">No. of Rooms <span className="text-danger">*</span></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Enter number of rooms"
                                            name="franchise_no_rooms"
                                            value={formData.franchise_no_rooms}
                                            onChange={handleInputChange}
                                        />
                                        {errors.franchise_no_rooms && <small className="text-danger">{errors.franchise_no_rooms}</small>}
                                    </div>
                                </div>

                                <div className="col-lg-4 col-md-4 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">Total Area (sq. ft) <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter total area"
                                            name="franchise_total_area"
                                            value={formData.franchise_total_area}
                                            onChange={handleInputChange}
                                        />
                                        {errors.franchise_total_area && <small className="text-danger">{errors.franchise_total_area}</small>}
                                    </div>
                                </div>

                                <div className="col-lg-4 col-md-4 col-sm-12">
                                    <div className="form-group">
                                        <label className="control-label">No. of Employees <span className="text-danger">*</span></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Enter number of employees"
                                            name="franchise_no_of_employees"
                                            value={formData.franchise_no_of_employees}
                                            onChange={handleInputChange}
                                        />
                                        {errors.franchise_no_of_employees && <small className="text-danger">{errors.franchise_no_of_employees}</small>}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-12 col-md-12 col-sm-12">
                                    <div className="pLace-order mt-30">
                                        <button className="view-all-accent-btn" type="submit" disabled={loading}>
                                            {loading ? "Submitting..." : "Submit & Continue to Director"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ATCRegistrationForm;